import { ChangeEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  ImageUp,
  Pencil,
  Plus,
  Send,
  Trash2,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';

import { useWorkspace } from '../../hooks/useWorkspace';
import { useUserProfile } from '../../hooks/useUserProfile';
import { useBookingLink } from '../../hooks/useBookingLink';
import { isValidBookingUrl, normalizeBookingUrl } from '../../utils/bookingLink';
import {
  useEmailConnections,
  emailConnectionKeys,
} from '../../hooks/useEmailConnections';
import type { EmailProvider } from '../../services/email-connection.service';
import { GmailIcon, OutlookIcon } from '../../components/icons/MailProviderIcons';
import { Button } from '../../components/ui/atoms/Button';
import { Input } from '@/components/ui/atoms/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Sheet } from '../../components/ui/organisms/Sheet';
import { Tooltip } from '../../components/ui/tooltip';
import {
  StatusPill,
  getRoleVariant,
} from '../../components/ui/atoms/StatusPill';
import { Skeleton } from '../../components/ui/atoms/Skeleton';
import { SendInvitationConfirmationDialog } from '../../components/workspace/SendInvitationConfirmationDialog';
import { LanguageSwitchConfirmDialog } from '../../components/i18n/LanguageSwitchConfirmDialog';
import { LOCALE_FULL_NAMES, SUPPORTED_LOCALES } from '../../i18n/config';
import type { Locale } from '../../i18n/config';

const DEFAULT_EMAIL_BUTTON_COLOR = '#D9ADFF';
const DEFAULT_EMAIL_BUTTON_TEXT_COLOR = '#000000';
const EMAIL_BUTTON_TEXT_COLOR_WHITE = '#FFFFFF';

const FieldRow = ({ label, value }: { label: string; value: ReactNode | null | undefined }) => {
  const isEmpty =
    value == null || (typeof value === 'string' && value.trim().length === 0);
  return (
    <div className="flex flex-col gap-0.5 py-2">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-dark-invirtus/45 font-body">
        {label}
      </span>
      <span className="text-sm text-dark-invirtus font-body break-words">
        {isEmpty ? <span className="text-dark-invirtus/40">-</span> : value}
      </span>
    </div>
  );
};

const SettingsSectionCard = ({
  title,
  description,
  action,
  children,
  className,
}: {
  title: string;
  description: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) => (
  <div className={`h-full rounded-xl border border-light-lila/40 bg-light p-5 flex flex-col ${className ?? ''}`}>
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <h6 className="text-base font-semibold text-dark-invirtus font-heading leading-tight">
          {title}
        </h6>
        <p className="mt-1 text-sm text-dark-invirtus/60 font-body">{description}</p>
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
    <div className="mt-5 flex-1 min-h-0">{children}</div>
  </div>
);

const WorkspaceSection = () => {
  const { t } = useTranslation(['settings', 'common']);
  const {
    data: workspace,
    isLoading: wsLoading,
    setLogoMutation,
    setBrandingMutation,
  } = useWorkspace();
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const [workspaceCompanyDraft, setWorkspaceCompanyDraft] = useState('');
  const [workspaceColorDraft, setWorkspaceColorDraft] = useState(DEFAULT_EMAIL_BUTTON_COLOR);
  const [useDefaultWorkspaceColor, setUseDefaultWorkspaceColor] = useState(true);
  const [workspaceSecondaryColorDraft, setWorkspaceSecondaryColorDraft] = useState(
    DEFAULT_EMAIL_BUTTON_TEXT_COLOR,
  );
  const [useDefaultWorkspaceSecondaryColor, setUseDefaultWorkspaceSecondaryColor] = useState(true);
  const [supportCcEnabledDraft, setSupportCcEnabledDraft] = useState(true);
  const [workspaceLogoDraftFile, setWorkspaceLogoDraftFile] = useState<File | null>(null);
  const [workspaceLogoDraftPreviewUrl, setWorkspaceLogoDraftPreviewUrl] = useState<string | null>(null);
  const [workspaceLogoDraftObjectUrl, setWorkspaceLogoDraftObjectUrl] = useState<string | null>(null);
  const [workspaceLanguageDraft, setWorkspaceLanguageDraft] = useState<Locale>('en');
  const [pendingLanguageConfirm, setPendingLanguageConfirm] = useState<Locale | null>(null);
  const [workspaceBrandingOpen, setWorkspaceBrandingOpen] = useState(false);
  const isSavingWorkspaceBranding =
    setLogoMutation.isPending || setBrandingMutation.isPending;

  useEffect(() => {
    if (!workspace) return;
    setWorkspaceCompanyDraft(workspace.company_name ?? '');
    setWorkspaceColorDraft(workspace.primary_color ?? DEFAULT_EMAIL_BUTTON_COLOR);
    setUseDefaultWorkspaceColor(!workspace.primary_color);
    setWorkspaceSecondaryColorDraft(workspace.secondary_color ?? DEFAULT_EMAIL_BUTTON_TEXT_COLOR);
    setUseDefaultWorkspaceSecondaryColor(!workspace.secondary_color);
    setSupportCcEnabledDraft(workspace.cc_invirtus_support_enabled);
    setWorkspaceLanguageDraft(workspace.default_language);
    setWorkspaceLogoDraftFile(null);
    setWorkspaceLogoDraftPreviewUrl(workspace.workspace_logo_url ?? null);
    if (workspaceLogoDraftObjectUrl) {
      URL.revokeObjectURL(workspaceLogoDraftObjectUrl);
      setWorkspaceLogoDraftObjectUrl(null);
    }
  }, [
    workspace?.workspace_name,
    workspace?.company_name,
    workspace?.primary_color,
    workspace?.secondary_color,
    workspace?.cc_invirtus_support_enabled,
    workspace?.default_language,
    workspace?.workspace_logo_url,
  ]);

  const handleSelectWorkspaceLogo = () => {
    if (setLogoMutation.isPending) return;
    logoInputRef.current?.click();
  };

  const handleWorkspaceLogoChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const filename = file.name.toLowerCase();
    const allowedExtensions = ['.png', '.jpg', '.jpeg', '.webp', '.svg'];
    const hasAllowedExtension = allowedExtensions.some((ext) => filename.endsWith(ext));
    if (!hasAllowedExtension) {
      toast.error(t('settings:workspace.logo_supported_formats_note'));
      event.target.value = '';
      return;
    }

    if (workspaceLogoDraftObjectUrl) {
      URL.revokeObjectURL(workspaceLogoDraftObjectUrl);
    }
    const objectUrl = URL.createObjectURL(file);
    setWorkspaceLogoDraftObjectUrl(objectUrl);
    setWorkspaceLogoDraftFile(file);
    setWorkspaceLogoDraftPreviewUrl(objectUrl);
    event.target.value = '';
  };

  const handleSaveWorkspaceBranding = async () => {
    const companyName = workspaceCompanyDraft.trim();
    if (!companyName) {
      toast.error(t('settings:workspace.name_required'));
      return;
    }

    const colorToSave = useDefaultWorkspaceColor
      ? null
      : workspaceColorDraft.trim().toUpperCase();
    const secondaryColorToSave = useDefaultWorkspaceSecondaryColor
      ? null
      : workspaceSecondaryColorDraft.trim().toUpperCase();
    try {
      if (workspaceLogoDraftFile) {
        await setLogoMutation.mutateAsync(workspaceLogoDraftFile);
      }
      await setBrandingMutation.mutateAsync({
        name: companyName,
        company_name: companyName || null,
        primary_color: colorToSave,
        secondary_color: secondaryColorToSave,
        cc_invirtus_support_enabled: supportCcEnabledDraft,
      });
      if (workspaceLogoDraftObjectUrl) {
        URL.revokeObjectURL(workspaceLogoDraftObjectUrl);
        setWorkspaceLogoDraftObjectUrl(null);
      }
      setWorkspaceLogoDraftFile(null);
      if (workspace && workspaceLanguageDraft !== workspace.default_language) {
        setPendingLanguageConfirm(workspaceLanguageDraft);
        return;
      }
      toast.success(t('settings:workspace.branding_updated'));
      setWorkspaceBrandingOpen(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t('settings:workspace.branding_update_error'),
      );
    }
  };

  const action = workspace ? (
    <Button
      label={t('settings:workspace.edit_branding')}
      type="outlined"
      intent="secondary"
      size="sm"
      icon={<Pencil/>}
      onClick={() => setWorkspaceBrandingOpen(true)}
      disabled={isSavingWorkspaceBranding}
    />
  ) : undefined;

  const previewPrimaryColor = useDefaultWorkspaceColor
    ? DEFAULT_EMAIL_BUTTON_COLOR
    : workspaceColorDraft;
  const previewSecondaryColor = useDefaultWorkspaceSecondaryColor
    ? DEFAULT_EMAIL_BUTTON_TEXT_COLOR
    : workspaceSecondaryColorDraft;

  return (
    <>
      <SettingsSectionCard
        title={t('settings:workspace.label')}
        description={t('settings:workspace.section_description')}
        action={action}
      >
        {wsLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : workspace ? (
          (() => {
            const companyName = (workspace.company_name ?? '').trim();
            const displayName = companyName || t('settings:workspace.default_name');

            return (
              <>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml,.png,.jpg,.jpeg,.webp,.svg"
                  className="hidden"
                  onChange={handleWorkspaceLogoChange}
                />
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                  <FieldRow
                    label={t('settings:workspace.company_logo_label')}
                    value={
                      <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-light-lila/60 bg-dark-invirtus-soft">
                        {workspace.workspace_logo_url ? (
                          <img
                            src={workspace.workspace_logo_url}
                            alt={t('settings:workspace.logo_alt', { workspaceName: displayName })}
                            className="h-full w-full rounded-xl object-contain"
                          />
                        ) : (
                          <Building2 className="h-6 w-6 text-dark-invirtus/40" />
                        )}
                      </span>
                    }
                  />
                  <FieldRow
                    label={t('settings:workspace.company_name_label')}
                    value={companyName || '-'}
                  />
                  <FieldRow
                    label={t('settings:language.title')}
                    value={LOCALE_FULL_NAMES[workspace.default_language] ?? workspace.default_language}
                  />
                  <FieldRow
                    label={t('settings:workspace.cc_support_label')}
                    value={
                      workspace.cc_invirtus_support_enabled
                        ? t('settings:workspace.cc_support_activated')
                        : t('settings:workspace.cc_support_deactivated')
                    }
                  />
                  <FieldRow
                    label={t('settings:workspace.primary_color_label')}
                    value={
                      <span className="inline-flex items-center gap-2">
                        <span
                          className="inline-block h-5 w-5 rounded-full border border-light-lila/70"
                          style={{ backgroundColor: workspace.primary_color ?? DEFAULT_EMAIL_BUTTON_COLOR }}
                        />
                        <span>
                          {workspace.primary_color
                            ? 'Custom'
                            : t('settings:workspace.default_value')}
                        </span>
                      </span>
                    }
                  />
                  <FieldRow
                    label={t('settings:workspace.secondary_color_label')}
                    value={
                      <span className="inline-flex items-center gap-2">
                        <span
                          className="inline-block h-5 w-5 rounded-full border border-light-lila/70"
                          style={{ backgroundColor: workspace.secondary_color ?? DEFAULT_EMAIL_BUTTON_TEXT_COLOR }}
                        />
                        <span>
                          {workspace.secondary_color
                            ? 'Custom'
                            : t('settings:workspace.default_value')}
                        </span>
                      </span>
                    }
                  />
                </div>
              </>
            );
          })()
        ) : null}
      </SettingsSectionCard>

      <Sheet
        open={workspaceBrandingOpen}
        onOpenChange={(open) => {
          if (!open) {
            setWorkspaceCompanyDraft(workspace?.company_name ?? '');
            setWorkspaceColorDraft(workspace?.primary_color ?? DEFAULT_EMAIL_BUTTON_COLOR);
            setUseDefaultWorkspaceColor(!workspace?.primary_color);
            setWorkspaceSecondaryColorDraft(
              workspace?.secondary_color ?? DEFAULT_EMAIL_BUTTON_TEXT_COLOR,
            );
            setUseDefaultWorkspaceSecondaryColor(!workspace?.secondary_color);
            setSupportCcEnabledDraft(workspace?.cc_invirtus_support_enabled ?? true);
            setWorkspaceLanguageDraft(workspace?.default_language ?? 'en');
            setWorkspaceLogoDraftFile(null);
            setWorkspaceLogoDraftPreviewUrl(workspace?.workspace_logo_url ?? null);
            setPendingLanguageConfirm(null);
            if (workspaceLogoDraftObjectUrl) {
              URL.revokeObjectURL(workspaceLogoDraftObjectUrl);
              setWorkspaceLogoDraftObjectUrl(null);
            }
          }
          setWorkspaceBrandingOpen(open);
        }}
        size="md"
      >
        <Sheet.Header>
          <Sheet.Title>{t('settings:workspace.branding_modal_title')}</Sheet.Title>
          <Sheet.Description>{t('settings:workspace.branding_modal_description')}</Sheet.Description>
        </Sheet.Header>
        <Sheet.Body>
          <div className="space-y-5">
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-dark-invirtus font-body">
                  {t('settings:workspace.company_logo_label')}
                </label>
                <div className="flex items-end gap-3">
                  <Tooltip content={t('settings:workspace.edit_logo')} variant="bottom" offset={4}>
                    <button
                      type="button"
                      onClick={handleSelectWorkspaceLogo}
                      disabled={setLogoMutation.isPending}
                      aria-label={t('settings:workspace.edit_logo')}
                      className="group relative shrink-0 flex h-16 w-16 items-center justify-center rounded-xl bg-dark-invirtus-soft border border-light-lila/60 hover:bg-dark-invirtus/10 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-80 disabled:hover:bg-dark-invirtus-soft"
                    >
                      {workspaceLogoDraftPreviewUrl ? (
                        <img
                          src={workspaceLogoDraftPreviewUrl}
                          alt={t('settings:workspace.logo_alt', { workspaceName: workspaceCompanyDraft || t('settings:workspace.default_name') })}
                          className="h-full w-full rounded-xl object-contain"
                        />
                      ) : (
                        <Building2 className="h-7 w-7 text-dark-invirtus/40" />
                      )}
                      <span className="absolute -bottom-1.5 -right-1.5 rounded-full bg-light border border-light-lila/60 p-1">
                        <ImageUp className="h-3 w-3 text-dark-invirtus/40" />
                      </span>
                    </button>
                  </Tooltip>
                  <div className="flex min-w-0 h-16 flex-col items-start justify-end gap-1">
                    <Button
                      label={t('settings:workspace.upload_logo')}
                      type="outlined"
                      intent="secondary"
                      size="sm"
                      onClick={handleSelectWorkspaceLogo}
                      disabled={setLogoMutation.isPending}
                    />
                    <p className="text-xs text-dark-invirtus/60 font-body">
                      {t('settings:workspace.logo_supported_formats_note')}
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-dark-invirtus font-body">
                  {t('settings:workspace.company_name_label')}
                </label>
                <Input
                  value={workspaceCompanyDraft}
                  onChange={(event) => setWorkspaceCompanyDraft(event.target.value)}
                  rounded="md"
                  placeholder={t('settings:workspace.company_name_placeholder')}
                />
              </div>
            </div>

            <div className="pt-1">
              <div className="flex items-center justify-between gap-4 rounded-lg border border-light-lila/50 bg-light px-3 py-2.5">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-dark-invirtus font-body">
                    {t('settings:language.title')}
                  </p>
                  <p className="text-xs text-dark-invirtus/60 font-body">
                    {t('settings:language.section_description')}
                  </p>
                </div>
                <Select
                  value={workspaceLanguageDraft}
                  onValueChange={(value) => setWorkspaceLanguageDraft(value as Locale)}
                >
                  <SelectTrigger className="!w-[110px] h-8 border-light-lila/60 bg-light px-2 text-sm text-dark-invirtus font-body focus:border-purple-soft focus:ring-purple-soft">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[210]">
                    {SUPPORTED_LOCALES.map((locale) => (
                      <SelectItem key={locale} value={locale} className="text-sm">
                        {LOCALE_FULL_NAMES[locale]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="pt-1">
              <div className="flex items-center justify-between gap-4 rounded-lg border border-light-lila/50 bg-light px-3 py-2.5">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-dark-invirtus font-body">
                    {t('settings:workspace.cc_support_label')}
                  </p>
                  <p className="text-xs text-dark-invirtus/60 font-body">
                    {t('settings:workspace.cc_support_description')}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-dark-invirtus/65 font-body">
                    {supportCcEnabledDraft
                      ? t('settings:workspace.cc_support_activated')
                      : t('settings:workspace.cc_support_deactivated')}
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={supportCcEnabledDraft}
                    aria-label={t('settings:workspace.cc_support_label')}
                    onClick={() => setSupportCcEnabledDraft((prev) => !prev)}
                    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-soft/60 ${
                      supportCcEnabledDraft
                        ? 'bg-purple border-purple'
                        : 'bg-light-lila/60 border-light-lila'
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 rounded-full bg-light shadow-sm transition-transform ${
                        supportCcEnabledDraft ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_250px] md:items-stretch">
              <div className="space-y-5">
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-dark-invirtus font-body">
                      {t('settings:workspace.primary_color_label')}
                    </label>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={useDefaultWorkspaceColor ? DEFAULT_EMAIL_BUTTON_COLOR : workspaceColorDraft}
                      onChange={(event) => {
                        setUseDefaultWorkspaceColor(false);
                        setWorkspaceColorDraft(event.target.value.toUpperCase());
                      }}
                      className="h-10 w-16 cursor-pointer rounded-md border border-light-lila/60 bg-transparent p-0 appearance-none overflow-hidden [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:border-0 [&::-webkit-color-swatch]:rounded-md [&::-moz-color-swatch]:border-0 [&::-moz-color-swatch]:border-none"
                    />
                    <div className="min-w-0 flex-1">
                      <Input
                        value={useDefaultWorkspaceColor ? DEFAULT_EMAIL_BUTTON_COLOR : workspaceColorDraft}
                        onChange={(event) => {
                          setUseDefaultWorkspaceColor(false);
                          setWorkspaceColorDraft(event.target.value);
                        }}
                        rounded="md"
                        className="font-mono"
                        placeholder="#D9ADFF"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-dark-invirtus font-body">
                      {t('settings:workspace.secondary_color_label')}
                    </label>
                  </div>
                  <div className="inline-flex w-full rounded-md border border-light-lila/60 bg-light overflow-hidden">
                    <button
                      type="button"
                      onClick={() => {
                        setUseDefaultWorkspaceSecondaryColor(true);
                        setWorkspaceSecondaryColorDraft(DEFAULT_EMAIL_BUTTON_TEXT_COLOR);
                      }}
                      className={`h-10 flex-1 text-sm font-medium font-body transition-colors border-r border-light-lila/60 ${
                        useDefaultWorkspaceSecondaryColor
                          ? 'bg-dark-invirtus text-light'
                          : 'bg-light text-dark-invirtus/75 hover:bg-dark-invirtus-soft'
                      }`}
                    >
                      {t('settings:workspace.text_color_black')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setUseDefaultWorkspaceSecondaryColor(false);
                        setWorkspaceSecondaryColorDraft(EMAIL_BUTTON_TEXT_COLOR_WHITE);
                      }}
                      className={`h-10 flex-1 text-sm font-medium font-body transition-colors ${
                        !useDefaultWorkspaceSecondaryColor
                        && workspaceSecondaryColorDraft.toUpperCase() === EMAIL_BUTTON_TEXT_COLOR_WHITE
                          ? 'bg-dark-invirtus text-light'
                          : 'bg-light text-dark-invirtus/75 hover:bg-dark-invirtus-soft'
                      }`}
                    >
                      {t('settings:workspace.text_color_white')}
                    </button>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-light-lila/50 bg-light px-3 py-3 text-center h-full flex flex-col items-center justify-center">
                <p className="mb-3 text-sm font-medium text-dark-invirtus font-body">
                  {t('settings:workspace.button_preview_label')}
                </p>
                <button
                  type="button"
                  disabled
                  style={{
                    backgroundColor: previewPrimaryColor,
                    color: previewSecondaryColor,
                  }}
                  className="inline-flex h-10 items-center justify-center rounded-md px-4 text-sm font-semibold border border-transparent mx-auto"
                >
                  {t('settings:workspace.button_preview_cta')}
                </button>
              </div>
            </div>
          </div>
        </Sheet.Body>
        <Sheet.Footer>
          <Button
            label={t('common:actions.cancel')}
            type="text"
            intent="secondary"
            onClick={() => setWorkspaceBrandingOpen(false)}
            disabled={isSavingWorkspaceBranding}
          />
          <Button
            label={t('common:actions.save')}
            type="contained"
            intent="secondary"
            onClick={handleSaveWorkspaceBranding}
            disabled={isSavingWorkspaceBranding}
            loading={isSavingWorkspaceBranding}
          />
        </Sheet.Footer>
      </Sheet>
      <LanguageSwitchConfirmDialog
        isOpen={pendingLanguageConfirm !== null}
        pendingLocale={pendingLanguageConfirm}
        onClose={() => setPendingLanguageConfirm(null)}
        onConfirmed={() => {
          toast.success(t('settings:workspace.branding_updated'));
          setWorkspaceBrandingOpen(false);
        }}
      />
    </>
  );
};

const TeamSection = ({
  workspace,
  isLoading,
  onInvite,
  inviteMutation,
}: {
  workspace: ReturnType<typeof useWorkspace>['data'];
  isLoading: boolean;
  onInvite: () => void;
  inviteMutation: ReturnType<typeof useWorkspace>['inviteMutation'];
}) => {
  const { t } = useTranslation('settings');
  const members = workspace?.members ?? [];
  const filtered = members;
  // Full-width card: members read as a two-column grid, and only a genuinely
  // long roster scrolls. A fixed card height used to clip the second row.
  const shouldScrollMembers = filtered.length > 8;

  return (
    <SettingsSectionCard
      title={t('team.title')}
      description={t('team.subtitle')}
      action={
        <Button
          label={t('team.invite_member')}
          icon={<Plus/>}
          type="outlined"
          intent="secondary"
          size="sm"
          onClick={onInvite}
          disabled={inviteMutation.isPending}
        />
      }
    >
      <div className="flex flex-col overflow-hidden rounded-xl border border-light-lila/40 bg-light">
        {isLoading ? (
          <div className="divide-y divide-light-lila/40">
            <Skeleton.Row />
            <Skeleton.Row />
            <Skeleton.Row />
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-6 py-10 text-center">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-dark-invirtus-soft mb-3">
              <Users className="h-5 w-5 text-dark-invirtus/60" />
            </div>
            <h6 className="text-sm font-semibold text-dark-invirtus font-heading">
              {t('team.empty_title')}
            </h6>
            <p className="mt-1 text-xs text-dark-invirtus/60 font-body">
              {t('team.empty_hint')}
            </p>
          </div>
        ) : (
          (() => {
            const membersList = (
              <ul className="divide-y divide-light-lila/40">
                {filtered.map((member) => {
                  const fullName = (member.full_name ?? '').trim();
                  const displayName =
                    fullName && fullName !== member.email
                      ? fullName
                      : member.email.split('@')[0];
                  const showEmail = displayName !== member.email;
                  const initials = displayName
                    .split(' ')
                    .map((w) => w.charAt(0))
                    .join('')
                    .toUpperCase()
                    .slice(0, 2);

                  return (
                    <li
                      key={member.id}
                      className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-dark-invirtus-soft/30"
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-purple-soft/30 text-dark-invirtus">
                        <span className="text-xs font-bold font-heading">{initials}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="truncate text-sm font-semibold text-dark-invirtus font-body">
                          {displayName}
                        </div>
                        {showEmail && (
                          <div className="truncate text-xs text-dark-invirtus/60 font-body">
                            {member.email}
                          </div>
                        )}
                      </div>
                      <StatusPill variant={getRoleVariant(member.role)}>
                        {member.role}
                      </StatusPill>
                    </li>
                  );
                })}
              </ul>
            );

            return shouldScrollMembers ? (
              <div className="max-h-[360px] overflow-y-auto">{membersList}</div>
            ) : (
              membersList
            );
          })()
        )}
      </div>
    </SettingsSectionCard>
  );
};

const EmailIntegrationsSection = () => {
  const { t } = useTranslation(['settings', 'common']);
  const { data, isLoading, startMutation, disconnectMutation } = useEmailConnections();

  const handleConnect = async (provider: EmailProvider) => {
    try {
      await startMutation.mutateAsync(provider);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('settings:email.connect_error'));
    }
  };

  const handleDisconnect = async (provider: EmailProvider) => {
    try {
      await disconnectMutation.mutateAsync(provider);
      toast.success(t('settings:email.disconnect_success'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('settings:email.disconnect_error'));
    }
  };

  const providers = data?.providers ?? [];

  return (
    <SettingsSectionCard
      title={t('settings:email.label')}
      description={t('settings:email.description')}
    >
      <div>
        {isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : !data?.feature_enabled ? (
          <p className="text-xs text-dark-invirtus/50 font-body">
            {t('settings:email.feature_disabled')}
          </p>
        ) : (
          <>
            <div className="divide-y divide-light-lila/30">
              {providers.map((p) => {
                const isGoogle = p.provider === 'google';
                const Icon = isGoogle ? GmailIcon : OutlookIcon;
                const title = isGoogle
                  ? t('settings:email.gmail_title')
                  : t('settings:email.outlook_title');
                const providerDescription = isGoogle
                  ? t('settings:email.gmail_description')
                  : t('settings:email.outlook_description');

                const isPending =
                  (startMutation.isPending && startMutation.variables === p.provider) ||
                  (disconnectMutation.isPending && disconnectMutation.variables === p.provider);
                const blockedByOther = !p.connected && !!data?.active_provider;
                const subtitle = !p.configured
                  ? t('settings:email.not_configured')
                  : p.connected && p.email_address
                    ? p.email_address
                    : providerDescription;

                return (
                  <div key={p.provider} className="flex items-center justify-between gap-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <Icon className="h-6 w-6 shrink-0" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-dark-invirtus font-body">
                            {title}
                          </span>
                          {p.connected && (
                            <StatusPill variant="success">
                              {t('settings:email.connected')}
                            </StatusPill>
                          )}
                        </div>
                        <div className="text-xs text-dark-invirtus/60 font-body truncate">
                          {subtitle}
                        </div>
                      </div>
                    </div>
                    <Button
                      label={p.connected ? t('settings:email.disconnect') : t('settings:email.connect')}
                      type="outlined"
                      intent="secondary"
                      size="sm"
                      disabled={!p.configured || isPending || blockedByOther}
                      loading={isPending}
                      title={blockedByOther ? t('settings:email.disconnect_first') : undefined}
                      onClick={() =>
                        p.connected ? handleDisconnect(p.provider) : handleConnect(p.provider)
                      }
                    />
                  </div>
                );
              })}
            </div>
            {data?.active_provider && (
              <p className="mt-3 text-[11px] text-dark-invirtus/45 font-body">
                {t('settings:email.single_active_hint')}
              </p>
            )}
          </>
        )}
      </div>
    </SettingsSectionCard>
  );
};

/**
 * The recruiter's default booking link — any provider, not just Calendly.
 *
 * User-level rather than workspace-level: two recruiters in one workspace each
 * book their own calendar. Read-only here with an Edit affordance, like every
 * other card on this page; the form lives in EditBookingLinkSheet.
 */
const BookingLinkSection = ({ onEdit }: { onEdit: () => void }) => {
  const { t } = useTranslation(['settings', 'common']);
  const { saved, isLoading } = useBookingLink();

  return (
    <SettingsSectionCard
      title={t('settings:booking_link.label')}
      description={t('settings:booking_link.section_description')}
      action={
        <Button
          label={t('settings:booking_link.edit')}
          type="outlined"
          intent="secondary"
          size="sm"
          icon={<Pencil />}
          onClick={onEdit}
          disabled={isLoading}
        />
      }
    >
      {isLoading ? (
        <Skeleton className="h-20 w-full" />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          <FieldRow label={t('settings:booking_link.url_label')} value={saved?.url} />
          <FieldRow
            label={t('settings:booking_link.provider_label')}
            value={saved?.provider_label}
          />
          <FieldRow
            label={t('settings:booking_link.include_label')}
            value={
              saved == null
                ? undefined
                : saved.include_by_default
                  ? t('settings:booking_link.included')
                  : t('settings:booking_link.not_included')
            }
          />
        </div>
      )}
    </SettingsSectionCard>
  );
};

interface EditBookingLinkSheetProps {
  open: boolean;
  onClose: () => void;
}

const EditBookingLinkSheet = ({ open, onClose }: EditBookingLinkSheetProps) => {
  const { t } = useTranslation(['settings', 'common']);
  const { saved, saveMutation, removeMutation } = useBookingLink();

  const [url, setUrl] = useState('');
  const [includeByDefault, setIncludeByDefault] = useState(true);

  useEffect(() => {
    if (!open) return;
    setUrl(saved?.url ?? '');
    setIncludeByDefault(saved?.include_by_default ?? true);
  }, [open, saved?.url, saved?.include_by_default]);

  const trimmed = url.trim();
  const showInvalid = trimmed.length > 0 && !isValidBookingUrl(trimmed);
  const isDirty =
    normalizeBookingUrl(trimmed) !== (saved?.url ?? '') ||
    includeByDefault !== (saved?.include_by_default ?? true);
  const isBusy = saveMutation.isPending || removeMutation.isPending;

  const handleSave = async () => {
    if (!isValidBookingUrl(trimmed)) {
      toast.error(t('settings:booking_link.invalid'));
      return;
    }
    try {
      await saveMutation.mutateAsync({
        url: normalizeBookingUrl(trimmed),
        include_by_default: includeByDefault,
      });
      toast.success(t('settings:booking_link.saved'));
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('settings:booking_link.save_failed'));
    }
  };

  const handleRemove = async () => {
    try {
      await removeMutation.mutateAsync();
      toast.success(t('settings:booking_link.removed'));
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('settings:booking_link.remove_failed'));
    }
  };

  const labelClass = 'block text-sm font-medium text-dark-invirtus mb-1.5 font-body';

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} size="md" dirty={isDirty}>
      <Sheet.Header>
        <Sheet.Title>{t('settings:booking_link.edit_title')}</Sheet.Title>
        <Sheet.Description>{t('settings:booking_link.edit_description')}</Sheet.Description>
      </Sheet.Header>
      <Sheet.Body>
        <div className="space-y-4">
          <div>
            <label htmlFor="bl-url" className={labelClass}>
              {t('settings:booking_link.url_label')}
            </label>
            <Input
              id="bl-url"
              type="url"
              value={url}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setUrl(e.target.value)}
              placeholder={t('settings:booking_link.placeholder')}
              rounded="md"
              disabled={isBusy}
            />
            <p
              className={`mt-1.5 text-xs font-body ${
                showInvalid ? 'text-red-600' : 'text-dark-invirtus/50'
              }`}
            >
              {showInvalid ? t('settings:booking_link.invalid') : t('settings:booking_link.hint')}
            </p>
          </div>
          <label className="flex items-start gap-2.5 pt-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeByDefault}
              onChange={(e) => setIncludeByDefault(e.target.checked)}
              disabled={isBusy}
              className="mt-0.5 h-4 w-4 rounded border-light-lila/60 text-purple focus:ring-purple-soft"
            />
            <span className="text-sm text-dark-invirtus font-body">
              {t('settings:booking_link.include_by_default')}
            </span>
          </label>
          <p className="text-xs font-body text-dark-invirtus/50">
            {t('settings:booking_link.usage_hint')}
          </p>
        </div>
      </Sheet.Body>
      <Sheet.Footer>
        {saved ? (
          <Button
            label={t('settings:booking_link.remove')}
            type="text"
            intent="danger"
            icon={<Trash2 />}
            onClick={handleRemove}
            disabled={isBusy}
          />
        ) : null}
        <Button label={t('common:actions.cancel')} type="text" intent="secondary" onClick={onClose} />
        <Button
          label={t('common:actions.save')}
          type="contained"
          intent="secondary"
          onClick={handleSave}
          disabled={isBusy || !isDirty || !isValidBookingUrl(trimmed)}
        />
      </Sheet.Footer>
    </Sheet>
  );
};

const ProfileSection = ({ onEditProfile }: { onEditProfile: () => void }) => {
  const { user } = useAuth0();
  const { t } = useTranslation(['settings', 'common']);
  const { data: profile, isLoading: profLoading } = useUserProfile();

  return (
    <SettingsSectionCard
      title={t('settings:profile.label')}
      description={t('settings:profile.section_description')}
      action={
        <Button
          label={t('settings:profile.edit')}
          type="outlined"
          intent="secondary"
          size="sm"
          icon={<Pencil/>}
          onClick={onEditProfile}
          disabled={profLoading}
        />
      }
    >
      {profLoading ? (
        <Skeleton className="h-20 w-full" />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          <FieldRow label={t('settings:profile.first_name')} value={profile?.first_name} />
          <FieldRow label={t('settings:profile.last_name')} value={profile?.last_name} />
          <FieldRow label={t('common:forms.email')} value={profile?.email || user?.email} />
          <FieldRow label={t('settings:profile.job_title')} value={profile?.job_title} />
          <FieldRow label={t('settings:profile.phone')} value={profile?.phone_number} />
          <FieldRow
            label={t('settings:profile.marketing_emails')}
            value={
              profile == null
                ? undefined
                : profile.marketing_consent
                  ? t('settings:profile.subscribed')
                  : t('settings:profile.not_subscribed')
            }
          />
        </div>
      )}
    </SettingsSectionCard>
  );
};

interface EditProfileSheetProps {
  open: boolean;
  onClose: () => void;
}

const EditProfileSheet = ({ open, onClose }: EditProfileSheetProps) => {
  const { t } = useTranslation(['settings', 'common']);
  const { data: profile, updateMutation } = useUserProfile();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);

  useEffect(() => {
    if (!open || !profile) return;
    setFirstName(profile.first_name ?? '');
    setLastName(profile.last_name ?? '');
    setJobTitle(profile.job_title ?? '');
    setPhoneNumber(profile.phone_number ?? '');
    setMarketingConsent(profile.marketing_consent);
  }, [open, profile]);

  const isDirty =
    !!profile &&
    (firstName !== (profile.first_name ?? '') ||
      lastName !== (profile.last_name ?? '') ||
      jobTitle !== (profile.job_title ?? '') ||
      phoneNumber !== (profile.phone_number ?? '') ||
      marketingConsent !== profile.marketing_consent);

  const handleSave = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast.error(t('settings:edit_profile.validation_required'));
      return;
    }
    try {
      await updateMutation.mutateAsync({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        job_title: jobTitle.trim(),
        phone_number: phoneNumber.trim() || undefined,
        marketing_consent: marketingConsent,
      });
      toast.success(t('settings:edit_profile.save_success'));
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('settings:edit_profile.save_error'));
    }
  };

  const labelClass = 'block text-sm font-medium text-dark-invirtus mb-1.5 font-body';

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} size="md" dirty={isDirty}>
      <Sheet.Header>
        <Sheet.Title>{t('settings:edit_profile.title')}</Sheet.Title>
        <Sheet.Description>{t('settings:edit_profile.description')}</Sheet.Description>
      </Sheet.Header>
      <Sheet.Body>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="ep-first" className={labelClass}>
                {t('settings:profile.first_name')}
              </label>
              <Input
                id="ep-first"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Adam"
                rounded="md"
              />
            </div>
            <div>
              <label htmlFor="ep-last" className={labelClass}>
                {t('settings:profile.last_name')}
              </label>
              <Input
                id="ep-last"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Mabrouk"
                rounded="md"
              />
            </div>
          </div>
          <div>
            <label htmlFor="ep-title" className={labelClass}>
              {t('settings:profile.job_title')}
            </label>
            <Input
              id="ep-title"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="Head of Talent"
              rounded="md"
            />
          </div>
          <div>
            <label htmlFor="ep-phone" className={labelClass}>
              {t('settings:profile.phone')}
            </label>
            <Input
              id="ep-phone"
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="+212 6 00 00 00 00"
              rounded="md"
            />
          </div>
          <label className="flex items-start gap-2.5 pt-2 cursor-pointer">
            <input
              type="checkbox"
              checked={marketingConsent}
              onChange={(e) => setMarketingConsent(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-light-lila/60 text-purple focus:ring-purple-soft"
            />
            <span className="text-sm text-dark-invirtus font-body">
              {t('settings:edit_profile.marketing_consent')}
            </span>
          </label>
        </div>
      </Sheet.Body>
      <Sheet.Footer>
        <Button label={t('common:actions.cancel')} type="text" intent="secondary" onClick={onClose} />
        <Button
          label={t('common:actions.save')}
          type="contained"
          intent="secondary"
          onClick={handleSave}
          disabled={!isDirty || updateMutation.isPending}
        />
      </Sheet.Footer>
    </Sheet>
  );
};

export const SettingsPage = () => {
  const { t } = useTranslation(['settings', 'common']);
  const { data: workspace, isLoading: wsLoading, inviteMutation } = useWorkspace();
  const queryClient = useQueryClient();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('email_connection');
    if (!result) return;

    if (result === 'success') {
      const email = params.get('email');
      toast.success(t('settings:email.connect_success', { email: email ?? '' }));
      queryClient.invalidateQueries({ queryKey: emailConnectionKeys.status() });
    } else if (result === 'error') {
      toast.error(params.get('reason') || t('settings:email.connect_error'));
    }

    const url = new URL(window.location.href);
    ['email_connection', 'provider', 'email', 'reason'].forEach((key) =>
      url.searchParams.delete(key),
    );
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [editBookingLinkOpen, setEditBookingLinkOpen] = useState(false);
  const [inviteSheetOpen, setInviteSheetOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');

  const closeInvite = () => {
    setInviteSheetOpen(false);
    setInviteEmail('');
  };

  const requestConfirm = () => {
    const email = inviteEmail.trim();
    if (!email) return;
    setPendingEmail(email);
    setInviteSheetOpen(false);
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    const email = pendingEmail.trim();
    if (!email) return;
    try {
      await inviteMutation.mutateAsync(email);
      toast.success(t('settings:invite.success', { email }), { duration: 3000 });
      setInviteEmail('');
      setPendingEmail('');
      setConfirmOpen(false);
    } catch (err) {
      setConfirmOpen(false);
      toast.error(err instanceof Error ? err.message : t('settings:invite.error'));
    }
  };

  const handleCancelConfirm = () => {
    if (inviteMutation.isPending) return;
    setConfirmOpen(false);
    setPendingEmail('');
  };

  return (
    <div className="bg-background-gray h-full overflow-y-auto">
      <div className="px-6 lg:px-10 py-6 md:py-10">
        <div className="mb-8 max-w-3xl">
          <h4 className="text-xl md:text-2xl font-semibold text-dark-invirtus font-heading leading-tight">
            {t('settings:page.title')}
          </h4>
          <p className="mt-1 text-sm text-dark-invirtus/60 font-body">
            {t('settings:page.subtitle')}
          </p>
        </div>

        <div className="w-full space-y-6">
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
            <div className="xl:col-span-6">
              <WorkspaceSection />
            </div>
            <div className="xl:col-span-6">
              <ProfileSection onEditProfile={() => setEditProfileOpen(true)} />
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
            <div className="xl:col-span-6">
              <EmailIntegrationsSection />
            </div>
            <div className="xl:col-span-6">
              <BookingLinkSection onEdit={() => setEditBookingLinkOpen(true)} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6">
            <TeamSection
              workspace={workspace}
              isLoading={wsLoading}
              onInvite={() => setInviteSheetOpen(true)}
              inviteMutation={inviteMutation}
            />
          </div>
        </div>
      </div>

      <EditProfileSheet open={editProfileOpen} onClose={() => setEditProfileOpen(false)} />
      <EditBookingLinkSheet
        open={editBookingLinkOpen}
        onClose={() => setEditBookingLinkOpen(false)}
      />

      <Sheet
        open={inviteSheetOpen}
        onOpenChange={(o) => (o ? setInviteSheetOpen(true) : closeInvite())}
        size="sm"
        dirty={inviteEmail.trim().length > 0}
      >
        <Sheet.Header>
          <Sheet.Title>{t('settings:invite.sheet_title')}</Sheet.Title>
          <Sheet.Description>{t('settings:invite.sheet_description')}</Sheet.Description>
        </Sheet.Header>
        <Sheet.Body>
          <label
            htmlFor="invite-email"
            className="block text-sm font-medium text-dark-invirtus mb-1.5 font-body"
          >
            {t('settings:invite.email_label')}
          </label>
          <Input
            id="invite-email"
            type="email"
            autoFocus
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && requestConfirm()}
            placeholder="name@company.com"
            rounded="md"
          />
        </Sheet.Body>
        <Sheet.Footer>
          <Button label={t('common:actions.cancel')} type="text" intent="secondary" onClick={closeInvite} />
          <Button
            label={t('settings:invite.send')}
            icon={<Send/>}
            type="contained"
            intent="secondary"
            onClick={requestConfirm}
            disabled={!inviteEmail.trim() || inviteMutation.isPending}
          />
        </Sheet.Footer>
      </Sheet>

      <SendInvitationConfirmationDialog
        open={confirmOpen}
        email={pendingEmail}
        isPending={inviteMutation.isPending}
        onClose={handleCancelConfirm}
        onConfirm={handleConfirm}
      />
    </div>
  );
};
